import React, { useState, useEffect } from "react";
import CompanyLayout from "../../components/layouts/CompanyLayout";
import { auth, firestore, database } from "../../firebaseConfig";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { ref, set, onValue, remove, get } from "firebase/database";
import toast from "react-hot-toast";
import {
  ShoppingBag,
  TrendingUp,
  Plus,
  Send,
  CheckCircle,
  XCircle,
  MapPin,
  Package,
} from "lucide-react";

const CompanyMarketplace = () => {
  const [activeTab, setActiveTab] = useState("marketplace");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [milkOffers, setMilkOffers] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);

  const tabs = [
    { id: "marketplace", label: "Milk Marketplace", icon: "🛒" },
    { id: "my-offers", label: "My Offers", icon: "📦" },
    { id: "my-requests", label: "My Requests", icon: "📨" },
    { id: "incoming", label: "Incoming Requests", icon: "📬" },
  ];

  // Fetch company name and data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setCompanyName(data.companyName);

          // Fetch companies list
          const companiesSnapshot = await getDocs(collection(firestore, "companies"));
          const companiesList = [];
          companiesSnapshot.forEach((doc) => {
            if (doc.id !== data.companyName) {
              companiesList.push({ id: doc.id, ...doc.data() });
            }
          });
          setCompanies(companiesList);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Listen to milk offers
  useEffect(() => {
    const offersRef = ref(database, "MilkMarketplace/Offers");
    const unsubscribe = onValue(offersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const offersList = Object.entries(data).map(([id, offer]) => ({
          id,
          ...offer,
        }));
        
        // Separate my offers from others
        const myOffersList = offersList.filter((o) => o.companyName === companyName);
        const marketplaceOffers = offersList.filter((o) => o.companyName !== companyName && o.status === "active");
        
        setMyOffers(myOffersList);
        setMilkOffers(marketplaceOffers);
      } else {
        setMyOffers([]);
        setMilkOffers([]);
      }
    });

    return () => unsubscribe();
  }, [companyName]);

  // Listen to my requests
  useEffect(() => {
    if (!companyName) return;

    const requestsRef = ref(database, `MilkMarketplace/Requests/${companyName}`);
    const unsubscribe = onValue(requestsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const requestsList = Object.entries(data).map(([id, request]) => ({
          id,
          ...request,
        }));
        setMyRequests(requestsList);
      } else {
        setMyRequests([]);
      }
    });

    return () => unsubscribe();
  }, [companyName]);

  // Listen to incoming requests
  useEffect(() => {
    if (!companyName) return;

    const incomingRef = ref(database, "MilkMarketplace/Requests");
    const unsubscribe = onValue(incomingRef, (snapshot) => {
      const data = snapshot.val();
      const incoming = [];
      
      if (data) {
        // Find requests where the target company is me
        Object.entries(data).forEach(([requesterCompany, requests]) => {
          Object.entries(requests).forEach(([requestId, request]) => {
            if (request.targetCompany === companyName && request.status === "pending") {
              incoming.push({
                id: requestId,
                requesterCompany,
                ...request,
              });
            }
          });
        });
      }
      
      setIncomingRequests(incoming);
    });

    return () => unsubscribe();
  }, [companyName]);

  if (loading) {
    return (
      <CompanyLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">Loading marketplace...</p>
        </div>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-cyan-700 flex items-center gap-2">
              <ShoppingBag size={32} />
              Milk Trading Marketplace
            </h1>
            <p className="text-gray-600 mt-2">
              Trade milk with other companies - sell surplus or buy when needed
            </p>
          </div>
          <button
            onClick={() => setShowOfferModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition"
          >
            <Plus size={18} />
            Create Offer
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-2 px-4 font-medium transition-all duration-200 flex items-center gap-2 ${
                activeTab === tab.id
                  ? "text-cyan-700 border-b-2 border-cyan-700"
                  : "text-gray-500 hover:text-cyan-600"
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
              {tab.id === "incoming" && incomingRequests.length > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                  {incomingRequests.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Marketplace Tab */}
        {activeTab === "marketplace" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {milkOffers.length === 0 ? (
              <div className="col-span-full bg-white rounded-lg shadow p-8 text-center">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No milk offers available at the moment</p>
              </div>
            ) : (
              milkOffers.map((offer) => (
                <MilkOfferCard
                  key={offer.id}
                  offer={offer}
                  onRequest={(offer) => {
                    setShowRequestModal(true);
                  }}
                  companyName={companyName}
                />
              ))
            )}
          </div>
        )}

        {/* My Offers Tab */}
        {activeTab === "my-offers" && (
          <div className="space-y-4">
            {myOffers.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">You haven't created any milk offers yet</p>
                <button
                  onClick={() => setShowOfferModal(true)}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition"
                >
                  Create First Offer
                </button>
              </div>
            ) : (
              myOffers.map((offer) => (
                <MyOfferCard
                  key={offer.id}
                  offer={offer}
                  onCancel={async (offerId) => {
                    try {
                      await remove(ref(database, `MilkMarketplace/Offers/${offerId}`));
                      toast.success("Offer cancelled");
                    } catch (error) {
                      toast.error("Failed to cancel offer");
                    }
                  }}
                />
              ))
            )}
          </div>
        )}

        {/* My Requests Tab */}
        {activeTab === "my-requests" && (
          <div className="space-y-4">
            {myRequests.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <Send className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">You haven't sent any milk requests yet</p>
              </div>
            ) : (
              myRequests.map((request) => (
                <RequestCard key={request.id} request={request} type="sent" />
              ))
            )}
          </div>
        )}

        {/* Incoming Requests Tab */}
        {activeTab === "incoming" && (
          <div className="space-y-4">
            {incomingRequests.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No incoming requests</p>
              </div>
            ) : (
              incomingRequests.map((request) => (
                <IncomingRequestCard
                  key={request.id}
                  request={request}
                  companyName={companyName}
                  onAccept={async (req) => {
                    try {
                      await set(
                        ref(
                          database,
                          `MilkMarketplace/Requests/${req.requesterCompany}/${req.id}/status`
                        ),
                        "accepted"
                      );
                      toast.success("Request accepted!");
                    } catch (error) {
                      toast.error("Failed to accept request");
                    }
                  }}
                  onDecline={async (req) => {
                    try {
                      await set(
                        ref(
                          database,
                          `MilkMarketplace/Requests/${req.requesterCompany}/${req.id}/status`
                        ),
                        "declined"
                      );
                      toast.success("Request declined");
                    } catch (error) {
                      toast.error("Failed to decline request");
                    }
                  }}
                />
              ))
            )}
          </div>
        )}

        {/* Create Offer Modal */}
        {showOfferModal && (
          <CreateOfferModal
            companyName={companyName}
            onClose={() => setShowOfferModal(false)}
          />
        )}

        {/* Send Request Modal */}
        {showRequestModal && (
          <SendRequestModal
            companyName={companyName}
            companies={companies}
            onClose={() => setShowRequestModal(false)}
          />
        )}
      </div>
    </CompanyLayout>
  );
};

// Milk Offer Card
const MilkOfferCard = ({ offer, onRequest, companyName }) => {
  const [showRequestModal, setShowRequestModal] = useState(false);

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{offer.companyName}</h3>
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            Available
          </span>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Quantity:</span>
            <span className="font-semibold text-cyan-700">{offer.quantity} Liters</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Price:</span>
            <span className="font-semibold text-gray-900">KES {offer.pricePerLiter}/L</span>
          </div>
          <div className="flex items-start gap-2">
            <MapPin size={16} className="text-gray-500 mt-1" />
            <div>
              <p className="text-sm text-gray-600">Pickup Location:</p>
              <p className="text-sm font-medium text-gray-800">{offer.pickupLocation}</p>
            </div>
          </div>
        </div>

        <button
          onClick={() => setShowRequestModal(true)}
          className="w-full mt-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition flex items-center justify-center gap-2"
        >
          <Send size={18} />
          Send Request
        </button>
      </div>

      {showRequestModal && (
        <QuickRequestModal
          offer={offer}
          companyName={companyName}
          onClose={() => setShowRequestModal(false)}
        />
      )}
    </>
  );
};

// My Offer Card
const MyOfferCard = ({ offer, onCancel }) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <Package className="text-cyan-600" size={24} />
            <h3 className="text-lg font-semibold text-gray-800">
              {offer.quantity} Liters Available
            </h3>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                offer.status === "active"
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {offer.status}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Price per Liter</p>
              <p className="font-semibold text-gray-900">KES {offer.pricePerLiter}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Pickup Location</p>
              <p className="font-medium text-gray-800">{offer.pickupLocation}</p>
            </div>
          </div>
        </div>

        <button
          onClick={() => onCancel(offer.id)}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
        >
          Cancel Offer
        </button>
      </div>
    </div>
  );
};

// Request Card (for sent requests)
const RequestCard = ({ request, type }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "accepted":
        return "bg-green-100 text-green-700";
      case "declined":
        return "bg-red-100 text-red-700";
      default:
        return "bg-amber-100 text-amber-700";
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <Send className="text-cyan-600" size={24} />
            <h3 className="text-lg font-semibold text-gray-800">
              Request to {request.targetCompany}
            </h3>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
              {request.status}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Quantity</p>
              <p className="font-semibold text-gray-900">{request.quantity} Liters</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Pickup Location</p>
              <p className="font-medium text-gray-800">{request.pickupLocation}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Requested</p>
              <p className="font-medium text-gray-800">
                {new Date(request.requestedAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Incoming Request Card
const IncomingRequestCard = ({ request, companyName, onAccept, onDecline }) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <Package className="text-cyan-600" size={24} />
            <h3 className="text-lg font-semibold text-gray-800">
              Request from {request.requesterCompany}
            </h3>
            <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
              Pending
            </span>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-600">Quantity</p>
              <p className="font-semibold text-gray-900">{request.quantity} Liters</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Pickup Location</p>
              <p className="font-medium text-gray-800">{request.pickupLocation}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Requested</p>
              <p className="font-medium text-gray-800">
                {new Date(request.requestedAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onDecline(request)}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center gap-2"
          >
            <XCircle size={16} />
            Decline
          </button>
          <button
            onClick={() => onAccept(request)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
          >
            <CheckCircle size={16} />
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

// Create Offer Modal
const CreateOfferModal = ({ companyName, onClose }) => {
  const [quantity, setQuantity] = useState("");
  const [pricePerLiter, setPricePerLiter] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!quantity || !pricePerLiter || !pickupLocation) {
      toast.error("Please fill all fields");
      return;
    }

    setSaving(true);
    try {
      // Sanitize company name for use in ID
      const sanitizedCompanyName = companyName.replace(/[^a-zA-Z0-9]/g, "_");
      const offerId = `${sanitizedCompanyName}_${Date.now()}`;
      await set(ref(database, `MilkMarketplace/Offers/${offerId}`), {
        companyName,
        quantity: parseFloat(quantity),
        pricePerLiter: parseFloat(pricePerLiter),
        pickupLocation,
        status: "active",
        createdAt: Date.now(),
      });

      toast.success("Offer created successfully!");
      onClose();
    } catch (error) {
      console.error("Error creating offer:", error);
      toast.error("Failed to create offer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Create Milk Offer</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity (Liters)
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              placeholder="e.g., 1000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price per Liter (KES)
            </label>
            <input
              type="number"
              value={pricePerLiter}
              onChange={(e) => setPricePerLiter(e.target.value)}
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              placeholder="e.g., 45.50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pickup Location
            </label>
            <input
              type="text"
              value={pickupLocation}
              onChange={(e) => setPickupLocation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              placeholder="e.g., Nairobi Collection Center"
            />
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              className={`flex-1 px-4 py-2 rounded-lg text-white transition ${
                saving ? "bg-cyan-300" : "bg-cyan-600 hover:bg-cyan-700"
              }`}
              disabled={saving}
            >
              {saving ? "Creating..." : "Create Offer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Quick Request Modal
const QuickRequestModal = ({ offer, companyName, onClose }) => {
  const [quantity, setQuantity] = useState("");
  const [pickupLocation, setPickupLocation] = useState(offer.pickupLocation);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!quantity || !pickupLocation) {
      toast.error("Please fill all fields");
      return;
    }

    setSending(true);
    try {
      // Sanitize company name for use in ID
      const sanitizedCompanyName = companyName.replace(/[^a-zA-Z0-9]/g, "_");
      const requestId = `${sanitizedCompanyName}_${Date.now()}`;
      await set(ref(database, `MilkMarketplace/Requests/${companyName}/${requestId}`), {
        targetCompany: offer.companyName,
        offerId: offer.id,
        quantity: parseFloat(quantity),
        pickupLocation,
        requestedAt: Date.now(),
        status: "pending",
      });

      toast.success("Request sent successfully!");
      onClose();
    } catch (error) {
      console.error("Error sending request:", error);
      toast.error("Failed to send request");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Request Milk from {offer.companyName}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity Needed (Liters)
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              max={offer.quantity}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              placeholder={`Max: ${offer.quantity}`}
            />
            <p className="text-xs text-gray-500 mt-1">
              Available: {offer.quantity} Liters @ KES {offer.pricePerLiter}/L
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pickup Location
            </label>
            <input
              type="text"
              value={pickupLocation}
              onChange={(e) => setPickupLocation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              disabled={sending}
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              className={`flex-1 px-4 py-2 rounded-lg text-white transition ${
                sending ? "bg-cyan-300" : "bg-cyan-600 hover:bg-cyan-700"
              }`}
              disabled={sending}
            >
              {sending ? "Sending..." : "Send Request"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Send Request Modal (standalone)
const SendRequestModal = ({ companyName, companies, onClose }) => {
  const [targetCompany, setTargetCompany] = useState("");
  const [quantity, setQuantity] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!targetCompany || !quantity || !pickupLocation) {
      toast.error("Please fill all fields");
      return;
    }

    setSending(true);
    try {
      // Sanitize company name for use in ID
      const sanitizedCompanyName = companyName.replace(/[^a-zA-Z0-9]/g, "_");
      const requestId = `${sanitizedCompanyName}_${Date.now()}`;
      await set(ref(database, `MilkMarketplace/Requests/${companyName}/${requestId}`), {
        targetCompany,
        quantity: parseFloat(quantity),
        pickupLocation,
        requestedAt: Date.now(),
        status: "pending",
      });

      toast.success("Request sent successfully!");
      onClose();
    } catch (error) {
      console.error("Error sending request:", error);
      toast.error("Failed to send request");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Request Milk</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Company
            </label>
            <select
              value={targetCompany}
              onChange={(e) => setTargetCompany(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">Select company...</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.companyName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity Needed (Liters)
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              placeholder="e.g., 500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pickup Location
            </label>
            <input
              type="text"
              value={pickupLocation}
              onChange={(e) => setPickupLocation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              placeholder="e.g., Nairobi Collection Center"
            />
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              disabled={sending}
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              className={`flex-1 px-4 py-2 rounded-lg text-white transition ${
                sending ? "bg-cyan-300" : "bg-cyan-600 hover:bg-cyan-700"
              }`}
              disabled={sending}
            >
              {sending ? "Sending..." : "Send Request"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyMarketplace;
